import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { FiHeart, FiMessageSquare, FiMoreVertical, FiEdit2, FiTrash2, FiX, FiImage, FiCrop, FiAlertTriangle, FiSlash, FiSmile } from 'react-icons/fi';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import Picker, { Emoji, EmojiStyle } from 'emoji-picker-react';
import MessageModal from '../../components/dashboard/MessageModal.js';
import ReportModal from '../../components/dashboard/ReportModal.js';
import ConfirmationModal from '../../components/admin/ConfirmationModal.js';
import { API_BASE_URL, buildMediaUrl } from '../../config/api';
import { fetchJson, formatFetchError } from '../../utils/fetchJson';

const Posts = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { success, error } = useToast();
  const [posts, setPosts] = useState(() => {
    try {
      const cached = localStorage.getItem('cached_posts_feed');
      return cached ? JSON.parse(cached) : [];
    } catch {
      return [];
    }
  });
  const [loading, setLoading] = useState(() => !localStorage.getItem('cached_posts_feed')); // Only show loading if no cache

  const [feedCursor, setFeedCursor] = useState(null);
  const [hasMorePosts, setHasMorePosts] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [likePending, setLikePending] = useState({});
  const MAX_POST_LENGTH = 1000;
  const POST_IMAGE_MAX_MB = 15;

  const [newPost, setNewPost] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageUrl, setSelectedImageUrl] = useState(null);
  const [showPostModal, setShowPostModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [showDropdown, setShowDropdown] = useState(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [cropImage, setCropImage] = useState(null);
  const [crop, setCrop] = useState({ unit: '%', width: 80, height: 80, x: 10, y: 10 });
  const [croppedImage, setCroppedImage] = useState(null);
  const [croppedBlob, setCroppedBlob] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [deletingPostId, setDeletingPostId] = useState(null);
  const fileInputRef = useRef(null);
  const cropImageRef = useRef(null);
  const textareaRef = useRef(null);
  const overlayRef = useRef(null);
  const [messageModal, setMessageModal] = useState({
    isOpen: false,
    recipient: null
  });
  const [reportModal, setReportModal] = useState({
    isOpen: false,
    postId: null,
    authorName: ''
  });
  const [blockModal, setBlockModal] = useState({
    isOpen: false,
    userId: null,
    userName: '',
    isBlocking: false
  });
  const [blockedUserIds, setBlockedUserIds] = useState(new Set());
  const [isReportSubmitting, setIsReportSubmitting] = useState(false);
  const [expandedPosts, setExpandedPosts] = useState({});
  const contentLength = newPost.trim().length;

  const getCountry = useCallback((profile = {}) => (
    profile.country ||
    profile.countryName ||
    profile.location?.country ||
    profile.address?.country ||
    null
  ), []);

  const normalizeGender = useCallback((gender) => {
    if (typeof gender !== 'string') return null;
    const value = gender.trim().toLowerCase();
    if (value === 'male' || value === 'female') return value;
    return null;
  }, []);

  const currentUserId = user?._id || user?.id;
  const userGender = user?.gender;
  const userCountry = getCountry(user);

  const isOppositeGenderPost = useCallback((authorGender) => {
    const viewerGender = normalizeGender(userGender);
    const author = normalizeGender(authorGender);

    if (!viewerGender || !author) return true;
    return viewerGender !== author;
  }, [normalizeGender, userGender]);

  const isSameCountryPost = useCallback((postCountry) => {
    // userCountry is redundant if we pass it, but this keeps closure clean
    if (!userCountry || !postCountry) return true; // If either missing, don't block visibility
    return postCountry.toLowerCase() === userCountry.toLowerCase();
  }, [userCountry]);

  const onEmojiClick = (emojiObject) => {
    setNewPost(prev => prev + emojiObject.emoji);
  };

  // Emoji rendering logic
  const emojiRegex = /(\p{Extended_Pictographic}(?:\uFE0F|\u200D\p{Extended_Pictographic})*)/gu;
  const isEmojiSegment = (segment) => {
    if (!segment) return false;
    emojiRegex.lastIndex = 0;
    return emojiRegex.test(segment);
  };
  const toUnified = (segment) => segment
    .split('\u200d')
    .map(part => [...part].map(ch => ch.codePointAt(0).toString(16)).join('-'))
    .join('-200d-');

  const renderEmojiText = (text) => {
    if (!text) return null;
    const parts = text.split(emojiRegex).filter(Boolean);
    return parts.map((part, idx) =>
      isEmojiSegment(part) ? (
        <span key={`emoji-${idx}`} className="inline-block px-px align-bottom">
          <Emoji
            unified={toUnified(part)}
            size={22}
            emojiStyle={EmojiStyle.FACEBOOK}
          />
        </span>
      ) : (
        <span key={`text-${idx}`}>{part}</span>
      )
    );
  };

  const handleTextareaScroll = (e) => {
    if (overlayRef.current) {
      overlayRef.current.scrollTop = e.target.scrollTop;
    }
  };



  const isPostExpanded = (postId) => !!expandedPosts[postId];
  const shouldTruncate = (content = '') => content.length > 220 || content.split('\n').length > 3;
  const togglePostExpand = (postId) => {
    setExpandedPosts(prev => ({
      ...prev,
      [postId]: !prev[postId]
    }));
  };

  const dispatchPostsUpdated = () => {
    window.dispatchEvent(new Event('posts_updated'));
  };

  // Fetch posts from backend
  const POSTS_PAGE_SIZE = 15;

  const mapPostsResponse = useCallback((items = []) => {
    if (!Array.isArray(items)) return [];
    return items
      .filter(post => post && post.author && post._id)
      .map(post => ({
        id: post._id,
        userId: post.author._id,
        userName: `${post.author.firstName} ${post.author.lastName}`,
        userPhoto: post.author.profilePhoto ? buildMediaUrl(post.author.profilePhoto) : null,
        authorGender: post.author.gender,
        country: getCountry(post.author),
        content: post.content,
        image: post.image ? buildMediaUrl(post.image) : null,
        timestamp: post.timestampFormatted,
        likes: post.likesCount,
        liked: post.likes.some(like => like._id === currentUserId),
        isOwnPost: post.author._id === currentUserId
      })).filter(post => post.isOwnPost || (isSameCountryPost(post.country) && isOppositeGenderPost(post.authorGender)));
  }, [getCountry, isSameCountryPost, isOppositeGenderPost, currentUserId]);

  const fetchPosts = useCallback(async () => {
    try {
      // Don't set loading(true) here to avoid flashing skeleton on re-fetches
      // Initial loading state is handled by useState(true)
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      params.set('limit', String(POSTS_PAGE_SIZE));
      const { response, data } = await fetchJson(`${API_BASE_URL}/api/posts?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok && data?.data) {
        const mapped = mapPostsResponse(data.data);
        setPosts(mapped);
        localStorage.setItem('cached_posts_feed', JSON.stringify(mapped)); // Cache updates
        const nextCursor = data?.pagination?.nextCursor ?? null;
        setFeedCursor(nextCursor);
        setHasMorePosts(Boolean(data?.pagination?.hasMore));
      } else {
        error(data?.message || 'Failed to load posts');
      }
    } catch (err) {
      console.error('Error fetching posts:', err);
      error(formatFetchError(err, 'Failed to load posts'));
    } finally {
      setLoading(false);
    }
  }, [POSTS_PAGE_SIZE, error, mapPostsResponse]);

  const loadMorePosts = useCallback(async () => {
    if (!hasMorePosts || loadingMore || !feedCursor) return;

    try {
      setLoadingMore(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      params.set('limit', String(POSTS_PAGE_SIZE));
      params.set('cursor', String(feedCursor));

      const { response, data } = await fetchJson(`${API_BASE_URL}/api/posts?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok && data?.data) {
        const mapped = mapPostsResponse(data.data);
        setPosts((prev) => {
          const seen = new Set(prev.map((p) => p.id));
          const deduped = mapped.filter((p) => !seen.has(p.id));
          return [...prev, ...deduped];
        });

        const nextCursor = data?.pagination?.nextCursor ?? null;
        setFeedCursor(nextCursor);
        setHasMorePosts(Boolean(data?.pagination?.hasMore));
      } else {
        error(data?.message || 'Failed to load more posts');
      }
    } catch (err) {
      console.error('Error loading more posts:', err);
      error(formatFetchError(err, 'Failed to load more posts'));
    } finally {
      setLoadingMore(false);
    }
  }, [POSTS_PAGE_SIZE, error, feedCursor, hasMorePosts, loadingMore, mapPostsResponse]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  useEffect(() => {
    return () => {
      if (selectedImageUrl) {
        URL.revokeObjectURL(selectedImageUrl);
      }
      if (croppedImage) {
        URL.revokeObjectURL(croppedImage);
      }
    };
  }, [selectedImageUrl, croppedImage]);

  useEffect(() => {
    const handlePostsUpdated = () => {
      setFeedCursor(null);
      setHasMorePosts(false);
      fetchPosts();
    };

    window.addEventListener('posts_updated', handlePostsUpdated);
    return () => {
      window.removeEventListener('posts_updated', handlePostsUpdated);
    };
  }, [fetchPosts]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside any dropdown
      const dropdowns = document.querySelectorAll('[data-dropdown]');
      let clickedOutside = true;

      dropdowns.forEach(dropdown => {
        if (dropdown.contains(event.target)) {
          clickedOutside = false;
        }
      });

      if (clickedOutside) {
        setShowDropdown(null);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const buildPostPayload = async () => {
    const content = newPost.trim();
    if (croppedImage || selectedImage) {
      const formData = new FormData();
      formData.append('content', content);
      if (croppedBlob) {
        formData.append('image', croppedBlob, 'post-image.jpg');
      } else if (selectedImage) {
        formData.append('image', selectedImage);
      }
      return {
        body: formData,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
        }
      };
    }

    return {
      body: JSON.stringify({ content }),
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        'Content-Type': 'application/json'
      }
    };
  };

  const handleCreatePost = async () => {
    const content = newPost.trim();
    if (!content && !selectedImage && !croppedImage) {
      error('Please write something or add an image to your post');
      return;
    }
    if (content.length > MAX_POST_LENGTH) {
      error(`Post is too long. Max ${MAX_POST_LENGTH} characters.`);
      return;
    }

    // Pre-post warning check
    const bannedKeywords = ['scam', 'fraud', 'money', 'buy', 'sell', 'bitcoin', 'crypto', 'nude', 'sex', 'sexual'];
    const foundBannedWord = bannedKeywords.find(word => content.toLowerCase().includes(word));

    if (foundBannedWord) {
      // Simple confirm dialog for now, or just an error
      const proceed = window.confirm(`Your post contains words that may violate our community guidelines ("${foundBannedWord}"). Are you sure you want to post this?`);
      if (!proceed) return;
    }

    try {
      setIsSubmitting(true);
      const payload = await buildPostPayload();
      const { response, data } = await fetchJson(`${API_BASE_URL}/api/posts`, {
        method: 'POST',
        ...payload
      });

      if (response.ok && data?.data) {
        const newPostData = {
          id: data.data._id,
          userId: data.data.author._id,
          userName: `${data.data.author.firstName} ${data.data.author.lastName}`,
          userPhoto: data.data.author.profilePhoto ? buildMediaUrl(data.data.author.profilePhoto) : null,
          country: getCountry(data.data.author),
          content: data.data.content,
          image: data.data.image ? buildMediaUrl(data.data.image) : null,
          timestamp: 'Just now',
          likes: data.data.likesCount,
          liked: false,
          isOwnPost: true
        };

        // Even though own post should always show, still respect country guard
        if (newPostData.isOwnPost || isSameCountryPost(newPostData.country)) {
          setPosts([newPostData, ...posts]);
        }
        setNewPost('');
        setSelectedImage(null);
        if (selectedImageUrl) {
          URL.revokeObjectURL(selectedImageUrl);
          setSelectedImageUrl(null);
        }
        setCroppedImage(null);
        setCroppedBlob(null);
        setShowPostModal(false);
        success('Post created successfully!');
        dispatchPostsUpdated();
      } else {
        error(data?.message || 'Failed to create post');
      }
    } catch (err) {
      console.error('Error creating post:', err);
      if (err?.status === 413) {
        error(`Image size should be less than ${POST_IMAGE_MAX_MB}MB`);
      } else {
        error(formatFetchError(err, 'Failed to create post'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLikePost = async (postId) => {
    if (!postId) return;
    if (likePending[postId]) return;

    let previousLiked = null;
    let previousLikes = null;
    let optimisticLiked = null;

    setLikePending((prev) => ({ ...prev, [postId]: true }));

    setPosts((prev) => prev.map((post) => {
      if (post.id !== postId) return post;

      previousLiked = Boolean(post.liked);
      previousLikes = Number.isFinite(post.likes) ? post.likes : 0;
      optimisticLiked = !previousLiked;

      const nextLikes = Math.max(0, previousLikes + (optimisticLiked ? 1 : -1));
      return {
        ...post,
        liked: optimisticLiked,
        likes: nextLikes
      };
    }));

    try {
      const token = localStorage.getItem('token');
      const { response, data } = await fetchJson(`${API_BASE_URL}/api/posts/${postId}/like`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok && data?.data) {
        const serverLikes = Number.isFinite(data.data.likesCount) ? data.data.likesCount : null;
        if (serverLikes !== null) {
          setPosts((prev) => prev.map((post) => (
            post.id === postId
              ? { ...post, likes: serverLikes, liked: optimisticLiked ?? post.liked }
              : post
          )));
        }
      } else {
        throw new Error(data?.message || 'Failed to update like');
      }
    } catch (err) {
      console.error('Error toggling like:', err);
      setPosts((prev) => prev.map((post) => (
        post.id === postId
          ? {
            ...post,
            liked: previousLiked ?? post.liked,
            likes: previousLikes ?? post.likes
          }
          : post
      )));
      error(formatFetchError(err, 'Failed to update like'));
    } finally {
      setLikePending((prev) => {
        const next = { ...prev };
        delete next[postId];
        return next;
      });
    }
  };

  const handleEditPost = (post) => {
    setEditingPost(post);
    setNewPost(post.content);
    setShowPostModal(true);
  };

  const handleUpdatePost = async () => {
    const content = newPost.trim();
    if (!content) {
      error('Post content cannot be empty');
      return;
    }
    if (content.length > MAX_POST_LENGTH) {
      error(`Post is too long. Max ${MAX_POST_LENGTH} characters.`);
      return;
    }

    try {
      setIsSubmitting(true);
      const token = localStorage.getItem('token');
      const { response, data } = await fetchJson(`${API_BASE_URL}/api/posts/${editingPost.id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content })
      });

      if (response.ok && data?.data) {
        setPosts(posts.map(post => {
          if (post.id === editingPost.id) {
            return {
              ...post,
              content: data.data.content,
              timestamp: 'Edited just now',
              edited: true
            };
          }
          return post;
        }));

        setShowPostModal(false);
        setEditingPost(null);
        setNewPost('');
        success('Post updated successfully!');
        dispatchPostsUpdated();
      } else {
        error(data?.message || 'Failed to update post');
      }
    } catch (err) {
      console.error('Error updating post:', err);
      error(formatFetchError(err, 'Failed to update post'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeletePost = async (postId) => {
    try {
      setDeletingPostId(postId);
      const token = localStorage.getItem('token');
      const { response, data } = await fetchJson(`${API_BASE_URL}/api/posts/${postId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok && (data?.success ?? true)) {
        setPosts(posts.filter(post => post.id !== postId));
        setShowDropdown(null);
        success('Post deleted successfully!');
        dispatchPostsUpdated();
      } else {
        error(data?.message || 'Failed to delete post');
      }
    } catch (err) {
      console.error('Error deleting post:', err);
      error(formatFetchError(err, 'Failed to delete post'));
    } finally {
      setDeletingPostId(null);
    }
  };

  const handleReportPost = (post) => {
    setReportModal({
      isOpen: true,
      postId: post.id,
      authorName: post.userName
    });
    setShowDropdown(null);
  };

  const handleBlockUser = (userId, userName) => {
    const isBlocked = blockedUserIds.has(userId);
    setBlockModal({
      isOpen: true,
      userId,
      userName,
      isBlocking: false,
      isUnblocking: isBlocked // Add flag to distinguish action
    });
    setShowDropdown(null);
  };

  const handleConfirmBlock = async () => {
    if (!blockModal.userId) return;

    const isUnblock = blockModal.isUnblocking;
    const endpoint = isUnblock ? 'unblock' : 'block';

    try {
      setBlockModal(prev => ({ ...prev, isBlocking: true }));
      const token = localStorage.getItem('token');
      const { response, data } = await fetchJson(`${API_BASE_URL}/api/user-profiles/${blockModal.userId}/${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok && (data?.success ?? true)) {
        success(`User ${isUnblock ? 'unblocked' : 'blocked'} successfully`);

        // Toggle blocked state locally without removing posts
        setBlockedUserIds(prev => {
          const next = new Set(prev);
          if (isUnblock) {
            next.delete(blockModal.userId);
          } else {
            next.add(blockModal.userId);
          }
          return next;
        });

      } else {
        error(data?.message || `Failed to ${isUnblock ? 'unblock' : 'block'} user`);
      }
    } catch (err) {
      console.error(`Error ${isUnblock ? 'unblocking' : 'blocking'} user:`, err);
      error(formatFetchError(err, `Failed to ${isUnblock ? 'unblock' : 'block'} user`));
    } finally {
      setBlockModal(prev => ({ ...prev, isBlocking: false, isOpen: false }));
    }
  };

  const handleSubmitReport = async (reportData) => {
    try {
      setIsReportSubmitting(true);
      const token = localStorage.getItem('token');
      const { response, data } = await fetchJson(`${API_BASE_URL}/api/posts/${reportModal.postId}/report`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(reportData)
      });

      if (response.ok && (data?.success ?? true)) {
        success('Report submitted successfully. Thank you for keeping our community safe.');
        setReportModal({ isOpen: false, postId: null, authorName: '' });
      } else {
        error(data?.message || 'Failed to submit report');
      }
    } catch (err) {
      console.error('Error submitting report:', err);
      error(formatFetchError(err, 'Failed to submit report'));
    } finally {
      setIsReportSubmitting(false);
    }
  };

  const handleDropdownClick = (e, postId) => {
    e.stopPropagation();
    setShowDropdown(showDropdown === postId ? null : postId);
  };

  const handleMessageUser = (postAuthor) => {
    // Create recipient object similar to UserProfile
    const recipient = {
      id: postAuthor.userId || postAuthor._id,
      _id: postAuthor.userId,
      firstName: postAuthor.userName.split(' ')[0],
      lastName: postAuthor.userName.split(' ')[1] || '',
      profilePhoto: postAuthor.userPhoto
    };

    setMessageModal({
      isOpen: true,
      recipient: recipient
    });
  };

  const handleCloseMessageModal = () => {
    setMessageModal({
      isOpen: false,
      recipient: null
    });
  };

  // MessageModal already sends the API request; here we just acknowledge success
  const handleSendMessage = () => {
    success('Message sent successfully!');
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > POST_IMAGE_MAX_MB * 1024 * 1024) {
        error(`Image size should be less than ${POST_IMAGE_MAX_MB}MB`);
        return;
      }

      if (selectedImageUrl) {
        URL.revokeObjectURL(selectedImageUrl);
      }
      if (croppedImage) {
        URL.revokeObjectURL(croppedImage);
      }

      const nextUrl = URL.createObjectURL(file);
      setSelectedImage(file);
      setSelectedImageUrl(nextUrl);
      setCroppedImage(null);
      setCroppedBlob(null);
    }
  };

  const handleOpenCrop = () => {
    if (!selectedImageUrl) return;
    setCrop({ unit: '%', width: 80, height: 80, x: 10, y: 10 });
    setCropImage(selectedImageUrl);
    setShowCropModal(true);
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setCroppedImage(null);
    setCroppedBlob(null);
    if (selectedImageUrl) {
      URL.revokeObjectURL(selectedImageUrl);
      setSelectedImageUrl(null);
    }
    if (croppedImage) {
      URL.revokeObjectURL(croppedImage);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCropConfirm = async () => {
    if (!cropImageRef.current || !crop?.width || !crop?.height) {
      setShowCropModal(false);
      setCropImage(null);
      return;
    }

    const img = cropImageRef.current;
    const naturalWidth = img.naturalWidth;
    const naturalHeight = img.naturalHeight;

    let pixelCrop;
    if (crop.unit === '%') {
      pixelCrop = {
        x: (crop.x / 100) * naturalWidth,
        y: (crop.y / 100) * naturalHeight,
        width: (crop.width / 100) * naturalWidth,
        height: (crop.height / 100) * naturalHeight
      };
    } else {
      const scaleX = naturalWidth / img.width;
      const scaleY = naturalHeight / img.height;
      pixelCrop = {
        x: crop.x * scaleX,
        y: crop.y * scaleY,
        width: crop.width * scaleX,
        height: crop.height * scaleY
      };
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      setShowCropModal(false);
      setCropImage(null);
      return;
    }

    const MAX_OUTPUT_SIZE = 1440;
    const cropWidth = Math.max(1, Math.round(pixelCrop.width));
    const cropHeight = Math.max(1, Math.round(pixelCrop.height));
    const scale = Math.min(1, MAX_OUTPUT_SIZE / Math.max(cropWidth, cropHeight));
    const outputWidth = Math.max(1, Math.round(cropWidth * scale));
    const outputHeight = Math.max(1, Math.round(cropHeight * scale));

    canvas.width = outputWidth;
    canvas.height = outputHeight;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    ctx.drawImage(
      img,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      outputWidth,
      outputHeight
    );

    const blob = await new Promise((resolve) => {
      canvas.toBlob((result) => resolve(result), 'image/jpeg', 0.95);
    });

    if (blob) {
      if (croppedImage) {
        URL.revokeObjectURL(croppedImage);
      }
      setCroppedBlob(blob);
      setCroppedImage(URL.createObjectURL(blob));
    }

    setShowCropModal(false);
    setCropImage(null);
  };

  const handleCropCancel = () => {
    setShowCropModal(false);
    setCropImage(null);
  };

  const openPostModal = () => {
    setEditingPost(null);
    setNewPost('');
    setSelectedImage(null);
    setCroppedImage(null);
    setCroppedBlob(null);
    if (selectedImageUrl) {
      URL.revokeObjectURL(selectedImageUrl);
      setSelectedImageUrl(null);
    }
    setShowPostModal(true);
  };

  const handleUserClick = (userId) => {
    navigate(`/dashboard/user-profile/${userId}`);
  };

  return (
    <div className="max-w-4xl mx-auto px-1 sm:px-4 lg:px-8 py-8">
      <div className="post-feed-shell">
        {/* Create Post Section */}
        <div className="post-card bg-white rounded-3xl shadow-soft p-6 mb-8">
          <div className="flex items-center space-x-4">

            <button
              onClick={openPostModal}
              className="post-input-trigger flex-1 text-left text-gray-500 bg-gray-100 rounded-xl px-4 py-3 hover:bg-gray-200 transition-colors"
            >
              Share your proposal details
            </button>
          </div>
        </div>

        {/* Posts Feed */}
        <div className="space-y-6">
          {loading ? (
            // Loading skeleton
            Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="post-card bg-white rounded-3xl shadow-soft overflow-hidden animate-pulse">
                <div className="p-6 border-b border-gray-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                      <div>
                        <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
                        <div className="h-3 bg-gray-200 rounded w-20"></div>
                      </div>
                    </div>
                    <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
                  </div>
                </div>
                <div className="p-6">
                  <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
                  <div className="h-48 bg-gray-200 rounded-xl mb-4"></div>
                </div>
                <div className="px-6 pb-6 flex items-center space-x-4">
                  <div className="w-20 h-8 bg-gray-200 rounded-lg"></div>
                  <div className="w-20 h-8 bg-gray-200 rounded-lg"></div>
                </div>
              </div>
            ))
          ) : posts.length === 0 ? (
            // Empty state
            <div className="bg-white rounded-3xl shadow-soft p-12 text-center">
              <div className="text-6xl mb-4">📝</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No posts yet</h3>
              <p className="text-gray-600">Be the first to share something with the community!</p>
            </div>
          ) : (
            // Posts
            posts.filter(p => p).map((post) => (
              <div key={post.id} className="post-card bg-white rounded-3xl shadow-soft overflow-hidden">
                {/* Post Header */}
                <div className="p-6 border-b border-gray-100">
                  <div className="flex items-center justify-between">
                    <div
                      className="flex items-center space-x-3 cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => handleUserClick(post.userId)}
                    >
                      <div className="w-10 h-10 bg-gray-200 rounded-full overflow-hidden">
                        {post.userPhoto ? (
                          <img
                            src={post.userPhoto}
                            alt={post.userName}
                            className="w-full h-full rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-r from-romantic-500 to-primary-500 flex items-center justify-center text-white font-semibold">
                            {post.userName.charAt(0)}
                          </div>
                        )}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{post.userName}</h3>
                        <p className="text-sm text-gray-500">{post.timestamp}</p>
                      </div>
                    </div>

                    {/* Dropdown Menu */}
                    <div className="relative" data-dropdown>
                      <button
                        onClick={(e) => handleDropdownClick(e, post.id)}
                        className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <FiMoreVertical className="text-gray-500" size={18} />
                      </button>

                      {showDropdown === post.id && (
                        <div className="absolute right-0 top-12 w-48 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-10 transition-all">
                          {post.isOwnPost ? (
                            <>
                              <button
                                onClick={() => {
                                  handleEditPost(post);
                                  setShowDropdown(null);
                                }}
                                className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-50 transition-colors flex items-center space-x-2"
                              >
                                <FiEdit2 size={16} />
                                <span>Edit Post</span>
                              </button>
                              <button
                                onClick={() => handleDeletePost(post.id)}
                                disabled={deletingPostId === post.id}
                                className={`w-full px-4 py-2 text-left transition-colors flex items-center space-x-2 ${deletingPostId === post.id
                                  ? 'text-red-300 cursor-not-allowed'
                                  : 'text-red-600 hover:bg-red-50'
                                  }`}
                              >
                                {deletingPostId === post.id ? (
                                  <>
                                    <svg className="w-4 h-4 animate-spin text-red-400" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h3z"></path>
                                    </svg>
                                    <span>Deleting…</span>
                                  </>
                                ) : (
                                  <>
                                    <FiTrash2 size={16} />
                                    <span>Delete Post</span>
                                  </>
                                )}
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => handleBlockUser(post.userId, post.userName)}
                                className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-50 transition-colors flex items-center space-x-2"
                              >
                                <FiSlash size={16} />
                                <span>{blockedUserIds.has(post.userId) ? 'Unblock User' : 'Block User'}</span>
                              </button>
                              <button
                                onClick={() => handleReportPost(post)}
                                className="w-full px-4 py-2 text-left text-red-600 hover:bg-red-50 transition-colors flex items-center space-x-2"
                              >
                                <FiAlertTriangle size={16} />
                                <span>Report Post</span>
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Post Content */}
                <div className="p-6">
                  {shouldTruncate(post.content) && !isPostExpanded(post.id) ? (
                    <>
                      <div className="text-gray-800 mb-2 line-clamp-3 whitespace-pre-line break-words">{renderEmojiText(post.content)}</div>
                      <button
                        onClick={() => togglePostExpand(post.id)}
                        className="text-romantic-600 text-sm font-semibold hover:underline"
                      >
                        Show more
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="text-gray-800 mb-4 whitespace-pre-line break-words">{renderEmojiText(post.content)}</div>
                      {shouldTruncate(post.content) && (
                        <button
                          onClick={() => togglePostExpand(post.id)}
                          className="text-romantic-600 text-sm font-semibold hover:underline -mt-2 mb-2"
                        >
                          Show less
                        </button>
                      )}
                    </>
                  )}

                  {post.image && (
                    <div className="rounded-xl overflow-hidden mb-4 bg-gray-50 border border-gray-100 flex items-center justify-center h-96">
                      <img
                        src={post.image}
                        alt="Post content"
                        className="w-auto h-auto max-w-full max-h-full object-contain"
                      />
                    </div>
                  )}
                </div>

                {/* Post Actions */}
                <div className="px-6 pb-6 flex items-center space-x-4">
                  <button
                    onClick={() => handleLikePost(post.id)}
                    disabled={Boolean(likePending[post.id])}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${post.liked
                      ? 'bg-romantic-50 text-romantic-600'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      } ${likePending[post.id] ? 'opacity-60 cursor-not-allowed' : ''}`}
                  >
                    <FiHeart className={post.liked ? 'fill-current' : ''} size={18} />
                    <span>{post.likes > 0 ? post.likes : 'Like'}</span>
                  </button>

                  <button
                    onClick={() => handleMessageUser(post)}
                    className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    <FiMessageSquare size={18} />
                    <span>Message</span>
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {!loading && posts.length > 0 && hasMorePosts && (
          <div className="flex justify-center pt-4">
            <button
              type="button"
              onClick={loadMorePosts}
              disabled={loadingMore}
              className="px-8 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {loadingMore && (
                <svg className="w-5 h-5 animate-spin text-gray-500" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h3z"></path>
                </svg>
              )}
              <span>{loadingMore ? 'Loading…' : 'Load more'}</span>
            </button>
          </div>
        )}

        {/* Create/Edit Post Modal */}
        {showPostModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-gray-900">
                    {editingPost ? 'Edit Post' : 'Create Post'}
                  </h2>
                  <button
                    onClick={() => setShowPostModal(false)}
                    className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <FiX className="text-gray-500" size={20} />
                  </button>
                </div>
              </div>

              <div className="p-6">
                <div className="relative mb-2">
                  {/* Visual Overlay */}
                  <div
                    ref={overlayRef}
                    className={`absolute inset-0 w-full px-4 pt-3 pb-12 border border-transparent rounded-xl whitespace-pre-wrap break-words overflow-hidden pointer-events-none text-gray-900 bg-white font-sans text-base leading-relaxed`}
                    style={{ zIndex: 0 }}
                  >
                    {newPost ? renderEmojiText(newPost) : <span className="text-gray-400">Share your proposal details</span>}
                  </div>

                  {/* Functional Textarea */}
                  <textarea
                    ref={textareaRef}
                    value={newPost}
                    onChange={(e) => setNewPost(e.target.value)}
                    onScroll={handleTextareaScroll}
                    placeholder="Share your proposal details"
                    className={`relative z-10 w-full px-4 pt-3 pb-12 border rounded-xl focus:ring-2 focus:ring-romantic-500 focus:border-transparent resize-none bg-transparent text-transparent caret-gray-900 placeholder-transparent font-sans text-base leading-relaxed ${contentLength > MAX_POST_LENGTH ? 'border-red-300' : 'border-gray-300'
                      }`}
                    rows={4}
                    maxLength={MAX_POST_LENGTH + 1}
                  />
                </div>
                <div className="flex items-center justify-between mt-2 text-sm">
                  {contentLength > MAX_POST_LENGTH ? (
                    <span className="text-red-600">Post is too long. Max {MAX_POST_LENGTH} characters.</span>
                  ) : (
                    <span className="text-gray-500">Keep it concise. Max {MAX_POST_LENGTH} characters.</span>
                  )}
                  <span className={`text-xs ${contentLength > MAX_POST_LENGTH ? 'text-red-600' : 'text-gray-500'}`}>
                    {contentLength}/{MAX_POST_LENGTH}
                  </span>
                </div>

                {/* Image Upload & Emoji */}
                <div className="mt-4">
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleImageSelect}
                    accept="image/*"
                    className="hidden"
                  />

                  <div className="flex items-center space-x-2 relative">
                    {!selectedImage && !croppedImage ? (
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
                      >
                        <FiImage size={18} />
                        <span>Add Photo</span>
                      </button>
                    ) : (
                      <div className="relative rounded-lg overflow-hidden bg-gray-50 border border-gray-100 flex items-center justify-center h-64 w-full">
                        <img
                          src={croppedImage || selectedImageUrl}
                          alt="Selected"
                          className="w-auto h-auto max-w-full max-h-full object-contain"
                        />
                        <button
                          type="button"
                          onClick={handleOpenCrop}
                          className="absolute top-2 right-12 p-2 bg-white/90 text-gray-700 rounded-lg hover:bg-white transition-colors border border-gray-200"
                          title={croppedImage ? 'Re-crop' : 'Crop'}
                        >
                          <FiCrop size={16} />
                        </button>
                        <button
                          onClick={handleRemoveImage}
                          className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                        >
                          <FiX size={16} />
                        </button>
                      </div>
                    )}

                    {/* Emoji Picker Button */}
                    <div className="relative">
                      <button
                        onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                        className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-700"
                        title="Add emoji"
                      >
                        <FiSmile size={24} />
                      </button>

                      {showEmojiPicker && (
                        <div className="absolute bottom-full left-0 mb-2 z-50">
                          <div className="bg-white rounded-lg shadow-xl border border-gray-200">
                            <div className="fixed inset-0 z-40" onClick={() => setShowEmojiPicker(false)} /> {/* Backdrop to close */}
                            <div className="relative z-50">
                              <Picker
                                onEmojiClick={onEmojiClick}
                                emojiStyle={EmojiStyle.FACEBOOK}
                                width={300}
                                height={400}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-6 border-t border-gray-200">
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setShowPostModal(false)}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={editingPost ? handleUpdatePost : handleCreatePost}
                    disabled={contentLength > MAX_POST_LENGTH || isSubmitting}
                    className="px-6 py-2 bg-romantic-500 text-white rounded-lg hover:bg-romantic-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                  >
                    {isSubmitting && (
                      <svg className="w-4 h-4 animate-spin text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h3z"></path>
                      </svg>
                    )}
                    <span>{editingPost ? 'Update' : (isSubmitting ? 'Posting...' : 'Post')}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Crop Modal */}
        {showCropModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="posts-crop-modal bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-gray-900 flex items-center space-x-2">
                    <FiCrop size={20} />
                    <span>Crop Image</span>
                  </h2>
                  <button
                    onClick={handleCropCancel}
                    className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <FiX className="text-gray-500" size={20} />
                  </button>
                </div>
              </div>

              <div className="p-6">
                <div className="posts-crop-stage w-full flex justify-center items-center" style={{ height: '55vh', padding: '24px', boxSizing: 'border-box' }}>
                  <ReactCrop
                    crop={crop}
                    onChange={(nextCrop, percentCrop) => setCrop(percentCrop || nextCrop)}
                    onComplete={(nextCrop, percentCrop) => setCrop(percentCrop || nextCrop)}
                    style={{ maxHeight: '55vh', maxWidth: '100%', overflow: 'visible', display: 'inline-block' }}
                  >
                    <img
                      ref={cropImageRef}
                      src={cropImage}
                      alt="Crop preview"
                      style={{ maxHeight: '55vh', maxWidth: '100%', width: 'auto', height: 'auto', objectFit: 'contain', display: 'block' }}
                    />
                  </ReactCrop>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    onClick={handleCropCancel}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCropConfirm}
                    className="px-6 py-2 bg-romantic-500 text-white rounded-lg hover:bg-romantic-600 transition-colors"
                  >
                    Crop & Continue
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Message Modal */}
        <MessageModal
          isOpen={messageModal.isOpen}
          onClose={handleCloseMessageModal}
          recipient={messageModal.recipient}
          currentUser={user}
          onSendMessage={handleSendMessage}
        />

        {/* Report Modal */}
        <ReportModal
          isOpen={reportModal.isOpen}
          onClose={() => setReportModal({ isOpen: false, postId: null, authorName: '' })}
          userName={reportModal.authorName}
          type="post"
          onSubmit={handleSubmitReport}
          isSubmitting={isReportSubmitting}
        />
      </div>
      <ReportModal
        isOpen={reportModal.isOpen}
        onClose={() => setReportModal({ ...reportModal, isOpen: false })}
        userName={reportModal.authorName}
        onSubmit={handleSubmitReport}
        isSubmitting={isReportSubmitting}
      />

      <ConfirmationModal
        isOpen={blockModal.isOpen}
        onClose={() => setBlockModal({ ...blockModal, isOpen: false })}
        onConfirm={handleConfirmBlock}
        title={blockModal.isUnblocking ? "Unblock User" : "Block User"}
        message={blockModal.isUnblocking
          ? `Are you sure you want to unblock ${blockModal.userName}? You will see their posts again.`
          : `Are you sure you want to block ${blockModal.userName}? You will no longer see their posts or be able to message them.`}
        confirmText={blockModal.isUnblocking ? "Unblock User" : "Block User"}
        cancelText="Cancel"
        isDestructive={!blockModal.isUnblocking}
        isLoading={blockModal.isBlocking}
      />
    </div>
  );
};

export default Posts;
